import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { ApiError } from '@/shared/api/api-error';

import { useCreateCategory, useUpdateCategory } from '../api/categories.queries';
import { categorySchema, type CategoryFormValues } from '../schemas/category.schema';
import type { Category } from '../types/category.types';

/**
 * Create / rename a category. Mount it only while open (the page does), so
 * the form always starts from the category being edited.
 */
export function CategoryFormDialog({
  category,
  onClose,
}: {
  /** Omit for create mode. */
  category?: Category;
  onClose: () => void;
}) {
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const mutation = category ? updateCategory : createCategory;
  const isEdit = category !== undefined;

  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: {
      nameEn: category?.nameEn ?? '',
      nameAr: category?.nameAr ?? '',
      isActive: false,
    },
  });

  // A name in use comes back as a field error on nameEn or nameAr.
  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = (field.charAt(0).toLowerCase() + field.slice(1)) as keyof CategoryFormValues;
      if (key in form.getValues()) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);

  const onSubmit = (values: CategoryFormValues) => {
    const payload = { nameEn: values.nameEn.trim(), nameAr: values.nameAr.trim() };
    if (category) {
      updateCategory.mutate({ categoryId: category.id, payload }, { onSuccess: onClose });
    } else {
      createCategory.mutate({ ...payload, isActive: values.isActive }, { onSuccess: onClose });
    }
  };

  const isPending = mutation.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isEdit ? `Edit ${category.nameEn ?? 'category'}` : 'Add category'}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Categories group brands on the storefront, in every country.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="nameEn"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>English name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Gaming" autoFocus disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="nameAr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arabic name</FormLabel>
                    <FormControl>
                      <Input {...field} dir="rtl" placeholder="ألعاب" disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isEdit && (
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="border-border flex flex-row items-center justify-between rounded-lg border p-3 sm:col-span-2">
                      <div className="space-y-0.5">
                        <FormLabel>Active on the storefront</FormLabel>
                        <FormDescription>
                          Leave off to add brands to it before customers see it.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isPending}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !form.formState.isDirty}>
                {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {isEdit ? 'Save changes' : 'Create category'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
